import { DownArrowIcon, RightArrowIcon, UpArrowIcon } from '@/Components/Common/Icons';
import { EngagementIcon, ChannelIcon } from '@/Components/Dashboard/icons';
import { useDashboardModal } from '@/Components/Dashboard/DashboardModal';
import useUserEventsByTypesAndTimeRange from '@/Components/Hooks/useUserEventsByTypesAndTimeRange';
import { EVENT_PROPERTIES, ENGAGEMENT_CARD_EVENTS, CHANNEL_APP_TYPES } from '@/constants/device';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { Card } from 'antd';
import { get, isEmpty } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import '../RecentActivity.less';
import {
  EmptyModalContent,
  ErrorModalContent,
  formatTime,
  getDateLabel,
  LoadingCard,
  LoadingModalContent,
  sortByLatestTimestamp,
} from '../RecentActivityUtils';

const ModalContent = ({ timezone, is24HourFormat, locale, data, isLoading, isError }) => {
  const trans = useSelector(translator);
  if (isLoading) return <LoadingModalContent />;

  if (isError) return <ErrorModalContent />;

  if (isEmpty(data)) return <EmptyModalContent />;

  const filteredData = data.filter((item) => item?.app_type !== CHANNEL_APP_TYPES.NATIVE);
  const rows = sortByLatestTimestamp(filteredData);
  return (
    <div className="recent-blood-pressure-table-card">
      <div className="recent-blood-pressure-table-head">
        <div className="head-cell head-cell-date">{trans('DATE_L')}</div>
        <div className="head-cell head-cell-bp">{trans('ACTIVITY')}</div>
      </div>

      <div className="recent-blood-pressure-table-body">
        {rows.map((entry, index) => {
          const timestamp = get(entry, EVENT_PROPERTIES.TIMESTAMP, '');
          const dateLabel = getDateLabel(timestamp, trans, timezone, locale);
          const formattedTime = formatTime(timestamp, is24HourFormat, timezone);
          const channel = get(entry, EVENT_PROPERTIES.CHANNEL_NAME, '');
          const appName = get(entry, EVENT_PROPERTIES.APP_NAME, '');

          return (
            <div
              className="recent-blood-pressure-table-row"
              key={`recent-blood-pressure-table-row-${index}`}
            >
              <div className="row-cell date-cell">
                <span className="date-text">{dateLabel}</span>
                <span className="time-text">{formattedTime}</span>
              </div>

              <div className="row-cell bp-cell">
                <ChannelIcon />
                <span>{isEmpty(channel) ? appName : channel}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

ModalContent.propTypes = {
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
};

const EngagementCard = (props) => {
  const { useruuid, timezone, locale, is24HourFormat, data, isLoading, trendData } = props;
  const trans = useSelector(translator);
  const { openDashboardModal } = useDashboardModal();
  const modalDataApi = useUserEventsByTypesAndTimeRange();
  const latestModalRequestIdRef = useRef(0);
  const invalidateModalRequest = useCallback(() => (latestModalRequestIdRef.current += 1), []);

  const getModalConfig = useCallback(
    (data = [], isLoading = true, isError = false, onClose = invalidateModalRequest) => ({
      title: (
        <div className="recent-activity-modal-title">
          <EngagementIcon className="recent-activity-modal-title-icon" fill="#005D98" />
          <span>{trans('ENGAGEMENT')}</span>
        </div>
      ),
      content: <ModalContent {...props} data={data} isLoading={isLoading} isError={isError} />,
      onClose,
    }),
    [invalidateModalRequest, props, trans]
  );

  const fetchModalData = (requestId) => {
    const startDate = moment()
      .subtract(7, 'days')
      .startOf('day')
      .format('YYYY-MM-DD[T]HH:mm:ss[Z]');
    const endDate = moment().endOf('day').format('YYYY-MM-DD[T]HH:mm:ss[Z]');
    const successCallback = (data) => {
      if (requestId !== latestModalRequestIdRef.current) {
        return;
      }

      openDashboardModal(getModalConfig(data, false));
    };
    const errorCallback = () => {
      if (requestId !== latestModalRequestIdRef.current) {
        return;
      }

      openDashboardModal(getModalConfig([], false, true));
    };
    modalDataApi.mutate({
      useruuid,
      startDateTimeUTC: startDate,
      endDateTimeUTC: endDate,
      eventTypes: ENGAGEMENT_CARD_EVENTS,
      successCallback,
      errorCallback,
    });
  };

  const handleCardClick = (e) => {
    e.preventDefault();
    const requestId = latestModalRequestIdRef.current + 1;
    latestModalRequestIdRef.current = requestId;
    openDashboardModal(getModalConfig());
    fetchModalData(requestId);
  };

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const requestId = latestModalRequestIdRef.current + 1;
      latestModalRequestIdRef.current = requestId;
      openDashboardModal(getModalConfig());
      fetchModalData(requestId);
    }
  };

  const removeNativeAppLaunch = (obj) => {
    if (obj?.app_launch_event?.app_type === CHANNEL_APP_TYPES.NATIVE) {
      delete obj.app_launch_event;
    }
    return obj;
  };

  const latestEvent = useMemo(() => {
    if (isEmpty(data)) return {};

    const cleaned = removeNativeAppLaunch(data);
    const entries = Object.values(cleaned);

    if (entries.length === 0) return {};

    return entries.reduce((latest, current) =>
      new Date(current.utc_timestamp) > new Date(latest.utc_timestamp) ? current : latest
    );
  }, [data]);

  if (isLoading) return <LoadingCard />;

  const displayText = get(latestEvent, EVENT_PROPERTIES.CHANNEL_NAME, '');
  const timestamp = get(latestEvent, EVENT_PROPERTIES.UTC_TIMESTAMP, '');
  const dateLabel = getDateLabel(timestamp, trans, timezone, locale);
  const formattedTime = formatTime(timestamp, is24HourFormat, timezone);
  const appName = get(latestEvent, EVENT_PROPERTIES.APP_NAME, '');

  const trendPercent = get(
    trendData,
    ['trendResults', 'engagement_bundle', 'engagement_bundle', 'trendPercent'],
    null
  );
  const isUp = trendPercent > 0;
  const isDown = trendPercent < 0;
  const displayPercent = trendPercent !== null ? Math.abs(trendPercent) : null;

  const CardTitle = (
    <div className="recent-activity-card-header">
      <EngagementIcon className="recent-activity-card-icon" fill="#005D98" />
      <h4 className="recent-activity-card-title">{trans('ENGAGEMENT')}</h4>
    </div>
  );

  return (
    <Card
      className="recent-activity-card"
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
      hoverable
      variant="borderless"
      title={CardTitle}
    >
      <div className="recent-activity-card-body">
        {timestamp && <p className="recent-activity-time">{`${dateLabel} ${formattedTime}`}</p>}
        {(displayText || appName) && (
          <p className="recent-activity-context">{isEmpty(displayText) ? appName : displayText}</p>
        )}
      </div>
      <>
        <hr className="recent-activity-divider" />
        <div className="recent-activity-footer-section">
          <p className="recent-activity-footer-label">{trans('CHANNEL_VIEWS')}</p>
          <div className="recent-activity-card-footer">
            <span className="recent-activity-comparison">
              {trans('YESTERDAY_VS_LAST_WEEK_AVG')}
            </span>
            {displayPercent !== null ? (
              <span
                className={`recent-activity-trend ${isUp ? 'up' : isDown ? 'down' : 'neutral'}`}
              >
                {isUp ? (
                  <UpArrowIcon className="trend-arrow" />
                ) : isDown ? (
                  <DownArrowIcon className="trend-arrow" />
                ) : (
                  <RightArrowIcon className="trend-arrow" />
                )}
                <span>{displayPercent}%</span>
              </span>
            ) : (
              <span className="recent-activity-trend neutral">-</span>
            )}
          </div>
        </div>
      </>
    </Card>
  );
};

EngagementCard.propTypes = {
  useruuid: PropTypes.string,
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  trendData: PropTypes.object,
  trendIsLoading: PropTypes.bool,
};

export default EngagementCard;
